import api from "../../../api";
import {
  mapPersonalSpaceFile,
  mapPersonalSpaceFolder,
  mapQaItem,
  mapTextItem,
  mapTrainedItem,
  mapUnorganizedItem,
} from "../utils/itemMappers";
import {
  getRepoItemBackendId,
  getRepoItemSource,
} from "../utils/repoItemIdentity";

export async function fetchRepoItems({
  role,
  isPersonalSpaceView,
  statusFilter,
  dayFilter,
  appliedSortOrder,
  signal,
}) {
  let personalSpaceRes = { data: { data: { folders: [], files: [] } } };
  if (isPersonalSpaceView) {
    const endpoint =
      statusFilter === "Saved" ? "/files/get" : "/files/inactive-files";
    const params = {
      page: 1,
      limit: 1000,
      search: "",
      dayFilter: dayFilter === "All" ? "" : dayFilter,
      sortOrder: appliedSortOrder === "Descending" ? "desc" : "asc",
      itemType: "all",
    };
    personalSpaceRes = await api.get(endpoint, { params, signal });
  }

  const [trainedRes, docsRes, textsRes, qaRes] = await Promise.all([
    api.get("/trained-documents/get-trained-docs", { signal }),
    role === "user" || role === "admin"
      ? api.get("/uploaded-files/global-user-docs", { signal })
      : Promise.resolve({ data: { data: [] } }),
    role === "admin"
      ? api.get(`${process.env.REACT_APP_API_URL}/uploaded-files/text`, {
          signal,
        })
      : Promise.resolve({ data: [] }),
    role === "admin"
      ? api.get(`${process.env.REACT_APP_API_URL}/uploaded-files/qa`, {
          signal,
        })
      : Promise.resolve({ data: [] }),
  ]);

  const organizedFilesSet = new Set(
    trainedRes.data
      .filter((item) => item.itemType === "file" && item.uploaded_file_id)
      .map((f) => String(f.uploaded_file_id)),
  );

  const allUserDocs = docsRes.data?.data || docsRes.data || [];
  const unorganizedFiles = allUserDocs.filter(
    (doc) =>
      !organizedFilesSet.has(String(doc.id || doc.file_id)) &&
      !organizedFilesSet.has(String(doc.trained_document_id)),
  );

  const trainedItems = trainedRes.data.map(mapTrainedItem);
  const unorganizedItems = unorganizedFiles.map(mapUnorganizedItem);

  const itemsMap = new Map();
  trainedItems.forEach((item) => itemsMap.set(item.id, item));
  unorganizedItems.forEach((item) => {
    if (!itemsMap.has(item.id)) itemsMap.set(item.id, item);
  });

  const personalSpaceData = personalSpaceRes.data?.data || {
    folders: [],
    files: [],
  };
  const psFolders = personalSpaceData.folders || [];
  const psFiles = personalSpaceData.files || [];

  psFolders.forEach((folder) => {
    const item = mapPersonalSpaceFolder(folder);
    itemsMap.set(item.id, item);
  });

  psFiles.forEach((doc) => {
    const item = mapPersonalSpaceFile(doc);
    itemsMap.set(item.id, item);
  });

  if (role === "admin") {
    console.log("RAW TEXT RESPONSE:", textsRes.data);
    console.log("RAW QA RESPONSE:", qaRes.data);
    (textsRes.data || []).forEach((textItem, index) => {
      const item = mapTextItem(textItem, index);
      itemsMap.set(item.id, item);
    });

    (qaRes.data || []).forEach((qaItem, index) => {
      const item = mapQaItem(qaItem, index);
      itemsMap.set(item.id, item);
    });
  }

  return Array.from(itemsMap.values());
}

export async function createPersonalSpaceFolder(folderName, currentFolder) {
  return api.post("/files/create-folder", {
    folder_name: folderName,
    parent_id: currentFolder
      ? currentFolder.replace("ps-folder-", "")
      : null,
  });
}

export async function createTrainedDocumentFolder(folderName, currentFolder) {
  return api.post("/trained-documents/create-folder", {
    name: folderName,
    parent_id: currentFolder,
  });
}

export async function renamePersonalSpaceItem(itemId, newName) {
  return api.put("/files/rename", {
    item_id: itemId,
    new_name: newName,
  });
}

export async function renameRepoItem(item, newName) {
  return api.put(`/trained-documents/rename/${getRepoItemBackendId(item)}`, {
    new_name: newName,
    item_source: getRepoItemSource(item),
    user_upload_type: item?.userUploadType || null,
  });
}

export async function markPersonalFileInactive(originalId) {
  return api.put(`/files/mark-inactive/${originalId}`);
}

export async function deletePersonalFile(originalId) {
  return api.delete(`/files/delete/${originalId}`);
}

export async function deleteTrainedDocuments(organizedIds, strategy) {
  return api.delete(`/trained-documents/${organizedIds[0]}`, {
    data: { item_ids: organizedIds, delete_strategy: strategy },
  });
}

export async function deleteUnorganizedDoc(fileId) {
  return api.delete(`/uploaded-files/docs/${fileId}`);
}

export async function downloadPersonalFile(originalId) {
  return api.get("/files/download", {
    params: { user_files_id: originalId },
    responseType: "blob",
  });
}

export async function downloadTrainedDocument(idToUse) {
  return api.get(`/trained-documents/download/${idToUse}`, {
    responseType: "blob",
  });
}

export async function bulkMovePersonalItems(itemIds, targetParentId) {
  return api.put("/files/bulk-move", {
    item_ids: itemIds,
    target_parent_id: targetParentId,
  });
}

export async function moveUnorganizedFiles(unorganizedIds, targetFolderId) {
  return api.put("/trained-documents/move-uploaded-files", {
    uploaded_file_ids: unorganizedIds,
    target_folder_id: targetFolderId,
  });
}

export async function moveOrganizedItems(organizedItems, targetFolderId) {
  return api.put("/trained-documents/move-items", {
    items: organizedItems,
    target_folder_id: targetFolderId,
  });
}

export async function revertPersonalFile(originalId) {
  return api.put(`/files/revert/${originalId}`);
}

export async function updateFileStatus(fileId, fileType, newStatus) {
  if (fileType === "qa" || fileType === "text") {
    return api.patch(
      `/uploaded-files/update_qa_status/${fileId}`,
      {
        upload_status: newStatus,
      },
    );
  }

  return api.patch(`/uploaded-files/update_file_status/${fileId}`);
}

export async function updateApprovalStatus(fileId, approvalStatus) {
  return api.post("/uploaded-files/approve-or-reject", {
    file_id: fileId,
    approval_status: approvalStatus,
    notes: "",
  });
}

export async function uploadPersonalFiles(formData) {
  return api.post("/files/upload-multiple", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
}

export async function downloadFileForTraining(originalId) {
  return api.get("/files/download", {
    params: { user_files_id: originalId },
    responseType: "blob",
  });
}

export async function uploadForTraining(formData) {
  return api.post("/uploaded-files/newQueryUpload", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
}

export function triggerBlobDownload(blob, fileName) {
  const url = window.URL.createObjectURL(new Blob([blob]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}
